import { Button, Chip, Switch } from "@heroui/react";
import { Segment } from "@heroui-pro/react";
import { ArrowRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import {
  ColumnsMenu,
  ComboField,
  DataTable,
  InlineFilterSelect,
  InitialsTile,
  SearchBox,
  notify,
  type DataGridColumn,
  type SortDescriptor,
} from "@/components/kit";
import { useGetAllBrands } from "@/hooks/api/brand/useGetAllBrands";
import { useUpdateScrapedBrand } from "@/hooks/api/scraped-brand/useUpdateScrapedBrand";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { ScrapedBrand } from "@/types/scraped-brand";

export interface ScrapedBrandTableProps {
  items: ScrapedBrand[];
  page: number;
  totalBrands: number;
  totalPage: number;
  changePage: (page: number) => void;
  isLoading: boolean;
  searchTerm: string;
  handleSearchTermChange: (searchTerm: string) => void;
  supplierId: string;
  handleSupplierIdChange: (supplierId: string) => void;
  blacklistFilter: string;
  handleBlacklistFilterChange: (value: string) => void;
  limit: number;
  handleLimitChange: (limit: number) => void;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => void;
}

const COLUMN_LABELS = [
  { id: "name", label: "Scraped brand" },
  { id: "linkedBrandId", label: "Linked brand" },
  { id: "productCount", label: "Products" },
  { id: "blacklist", label: "Blacklisted" },
  { id: "actions", label: "Actions" },
];

const BLACKLIST_TABS = [
  { id: "all", label: "All" },
  { id: "whitelisted", label: "Whitelisted" },
  { id: "blacklisted", label: "Blacklisted" },
];

export const ScrapedBrandTable = ({
  items,
  page,
  totalBrands,
  changePage,
  isLoading,
  searchTerm,
  handleSearchTermChange,
  supplierId,
  handleSupplierIdChange,
  blacklistFilter,
  handleBlacklistFilterChange,
  limit,
  handleLimitChange,
  sortDescriptor,
  onSortChange,
}: ScrapedBrandTableProps) => {
  const [visibleColumns, setVisibleColumns] = useState(
    () => new Set(COLUMN_LABELS.map((column) => column.id)),
  );

  const { data: suppliers } = useGetAllSuppliers();
  const { data: brands } = useGetAllBrands();

  const { mutate: updateScrapedBrand } = useUpdateScrapedBrand();

  const handleUpdateBlacklist = useCallback(
    (brand: ScrapedBrand, newValue: boolean) => {
      updateScrapedBrand(
        { id: brand.id, blacklist: newValue },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update brand ${brand.name}`,
              error.response?.data?.message ||
                "Failed to update blacklist status. Please try again.",
            );
          },
        },
      );
    },
    [updateScrapedBrand],
  );

  const handleUpdateLinkedBrand = useCallback(
    (brand: ScrapedBrand, linkedBrandId: string | null) => {
      updateScrapedBrand(
        { id: brand.id, linkedBrandId },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update brand ${brand.name}`,
              error.response?.data?.message ||
                "Failed to update linked brand. Please try again.",
            );
          },
        },
      );
    },
    [updateScrapedBrand],
  );

  // The cells are rebuilt whenever the supplier and brand lists arrive, so a row can never keep the empty list it was
  // first drawn with.
  const allColumns = useMemo<DataGridColumn<ScrapedBrand>[]>(
    () => [
      {
        id: "name",
        header: "Scraped brand",
        isRowHeader: true,
        minWidth: 240,
        cell: (brand) => {
          const supplier = suppliers?.find((s) => s.id === brand.supplierId);

          return (
            <div className="flex min-w-0 items-center gap-3">
              <InitialsTile name={brand.name} />
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="flex items-center gap-2">
                  <span className="truncate font-medium">{brand.name}</span>
                  {brand.blacklist ? (
                    <Chip color="danger" size="sm" variant="soft">
                      Blacklisted
                    </Chip>
                  ) : null}
                </span>
                <span className="text-muted truncate text-xs">
                  {supplier?.name || "Unknown Supplier"}
                  <span className="sr-only"> {brand.supplierId}</span>
                </span>
              </div>
            </div>
          );
        },
      },
      {
        id: "arrow",
        header: <span className="sr-only">Links to</span>,
        width: 36,
        cell: () => (
          <ArrowRight aria-hidden="true" className="text-muted size-4" />
        ),
      },
      {
        id: "linkedBrandId",
        header: "Linked brand",
        width: 230,
        cell: (brand) => (
          <ComboField
            hideLabel
            inline
            items={brands ?? []}
            label={`Linked brand for ${brand.name}`}
            placeholder="Not linked"
            value={brand.linkedBrandId ?? ""}
            onChange={(id) => handleUpdateLinkedBrand(brand, id || null)}
          />
        ),
      },
      {
        id: "productCount",
        header: "Products",
        align: "end",
        width: 130,
        allowsSorting: true,
        cell: (brand) => (
          <span className="font-medium tabular-nums">
            {brand.productCount.toLocaleString()}
          </span>
        ),
      },
      {
        id: "blacklist",
        header: "Blacklisted",
        width: 110,
        cell: (brand) => (
          <Switch
            aria-label={`Blacklisted: ${brand.name}`}
            isSelected={brand.blacklist}
            size="sm"
            onChange={(selected) => handleUpdateBlacklist(brand, selected)}
          >
            <Switch.Content>
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Content>
          </Switch>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        width: 160,
        align: "end",
        cell: (brand) => (
          <a
            className="button button--outline button--sm"
            href={`/scraped/products?brandIds=${brand.id}:${encodeURIComponent(brand.name)}`}
            rel="noreferrer"
            target="_blank"
          >
            Browse products
          </a>
        ),
      },
    ],
    [suppliers, brands, handleUpdateBlacklist, handleUpdateLinkedBrand],
  );

  const columns = allColumns.filter((column) =>
    column.id === "arrow"
      ? visibleColumns.has("linkedBrandId")
      : visibleColumns.has(column.id),
  );

  const hasFilters = Boolean(searchTerm || supplierId || blacklistFilter);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Segment
          aria-label="Blacklist status"
          selectedKey={blacklistFilter || "all"}
          onSelectionChange={(key) =>
            handleBlacklistFilterChange(key === "all" ? "" : String(key))
          }
        >
          {BLACKLIST_TABS.map((tab, index) => (
            <Segment.Item key={tab.id} id={tab.id}>
              {index > 0 ? <Segment.Separator /> : null}
              {tab.label}
            </Segment.Item>
          ))}
        </Segment>
        <div className="flex min-w-0 max-w-full items-center gap-2">
          <div>
            <InlineFilterSelect
              allLabel="All"
              label="Supplier"
              options={(suppliers ?? []).map((s) => ({
                id: s.id,
                label: s.name,
              }))}
              value={supplierId || null}
              onChange={(value) => handleSupplierIdChange(value ?? "")}
            />
          </div>
          <SearchBox
            className="w-64"
            placeholder="Search by name"
            value={searchTerm}
            onChange={handleSearchTermChange}
          />
          <ColumnsMenu
            columns={COLUMN_LABELS}
            visible={visibleColumns}
            onChange={setVisibleColumns}
          />
        </div>
      </div>

      <DataTable
        compact
        columns={columns}
        empty={{
          title: "No scraped brands found",
          description: "Try adjusting your search or filters.",
          action: hasFilters ? (
            <Button
              size="sm"
              variant="secondary"
              onPress={() => {
                handleSearchTermChange("");
                handleSupplierIdChange("");
                handleBlacklistFilterChange("");
              }}
            >
              Clear all filters
            </Button>
          ) : undefined,
        }}
        getRowId={(brand) => brand.id}
        isLoading={isLoading && items.length === 0}
        label="Scraped brands"
        minTableWidth={900}
        pagination={{
          page,
          pageSize: limit,
          total: totalBrands,
          onPageChange: changePage,
          onPageSizeChange: handleLimitChange,
          itemLabel: "brands",
        }}
        rows={items}
        sort={sortDescriptor}
        onSortChange={onSortChange}
      />
    </div>
  );
};
